import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Send, Bug } from "lucide-react";
import { useAuth } from "../lib/auth";
import { isInTelegram, initTG, hasTelegramInitData } from "../lib/telegram";
import { t } from "../lib/i18n";
import { toast } from "sonner";
import api, { setToken } from "../lib/api";

export default function Landing() {
  const { loginTelegram, setUser, setAuthed } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);

  useEffect(() => { initTG(); }, []);

  const handleTelegram = async () => {
    setBusy(true);
    try {
      const res = await loginTelegram();
      const nextUser = res?.user;
      if (nextUser?.is_active === false) {
        nav("/blocked", { replace: true });
        return;
      }
      if (!nextUser?.birth_date) nav("/onboarding", { replace: true });
      else if (!nextUser?.photos?.length) nav("/photos", { replace: true });
      else nav("/discover", { replace: true });
    } catch (e) {
      if (e?.code === "TG_INIT_MISSING") {
        toast.error("Откройте WebApp через кнопку в чате бота (не обычной ссылкой).", {
          action: {
            label: "Открыть бота",
            onClick: () => {
              if (typeof window !== "undefined") {
                window.location.href = "https://t.me/my_kupidon_bot";
              }
            },
          },
        });
      } else if (e?.code === "TG_INIT_INVALID") {
        toast.error("Telegram-авторизация не прошла. Закройте WebApp и откройте его снова из бота.");
      } else {
        toast.error("Не удалось войти через Telegram. Попробуйте снова.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="kupidon-shell grain safe-top safe-bottom flex flex-col items-stretch relative" data-testid="landing-page">
      {/* Hero */}
      <div className="relative flex-1 flex flex-col items-start justify-center px-7 py-10 z-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
          className="flex items-center gap-3 mb-8"
        >
          <div className="w-14 h-14 rounded-3xl bg-gradient-to-br from-[#F4A261] to-[#E76F51] shadow-lg shadow-[#E76F51]/30 flex items-center justify-center">
            <Heart className="w-7 h-7 text-white" fill="white" strokeWidth={0} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--ink-soft)]">Kupidon</div>
            <div className="font-[Onest] font-extrabold text-2xl -mt-1">REBORN</div>
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="font-[Onest] font-extrabold text-5xl leading-[1.02] tracking-tight text-[color:var(--ink)]"
        >
          Найди своё
          <br />
          <span className="text-[color:var(--peach-deep)]">«взаимно»</span>
          <br />
          рядом.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mt-5 text-[15px] leading-relaxed text-[color:var(--ink-soft)] max-w-[26ch]"
        >
          Знакомства в Telegram по-новому. Тёплые люди, честные фото, никакой сухой ленты.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.6 }}
          className="mt-10 flex flex-col gap-3 w-full"
        >
          <button
            data-testid="landing-telegram-btn"
            onClick={handleTelegram}
            disabled={busy}
            className="btn-pill bg-[color:var(--ink)] text-white flex items-center justify-center gap-2 shadow-lg shadow-black/10 hover:bg-[#2d251f] disabled:opacity-60"
          >
            <Send className="w-4 h-4" strokeWidth={2.5} />
            {t("ru", "continue_telegram")}
          </button>
          {!isInTelegram() && (
            <p className="text-xs text-[color:var(--ink-soft)] text-center mt-1">
              Для входа откройте приложение из кнопки в Telegram-боте.
            </p>
          )}
          {isInTelegram() && !hasTelegramInitData() && (
            <p className="text-xs text-[color:var(--ink-soft)] text-center mt-1">
              Telegram открыт, но данные Mini App не получены. Откройте приложение из кнопки в чате бота.
            </p>
          )}
          {!isInTelegram() && (
            <button
              data-testid="landing-dev-btn"
              onClick={async () => {
                setBusy(true);
                try {
                  const { data } = await api.post("/auth/dev", { first_name: "Тест" });
                  setToken(data.token);
                  setUser(data.user);
                  setAuthed(true);
                  if (!data.user?.birth_date) nav("/onboarding", { replace: true });
                  else if (!data.user?.photos?.length) nav("/photos", { replace: true });
                  else nav("/discover", { replace: true });
                } catch (e) {
                  toast.error("Dev вход не удался. Возможно DEV_MODE выключен.");
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy}
              className="btn-pill bg-white/70 text-[color:var(--ink)] border border-[color:var(--peach)] flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Bug className="w-4 h-4" strokeWidth={2.5} />
              Dev-вход (тест)
            </button>
          )}
        </motion.div>
      </div>

      {/* Floating decorative cards */}
      <motion.div
        initial={{ opacity: 0, rotate: -8, x: 40 }}
        animate={{ opacity: 1, rotate: -8, x: 0 }}
        transition={{ delay: 0.5, duration: 0.8 }}
        className="absolute right-[-40px] top-24 w-40 h-52 rounded-3xl bg-gradient-to-br from-[#FFE0CC] to-[#FFC5A5] shadow-xl shadow-[#E76F51]/30 z-0"
        aria-hidden
      />
      <motion.div
        initial={{ opacity: 0, rotate: 6, x: -40 }}
        animate={{ opacity: 1, rotate: 6, x: 0 }}
        transition={{ delay: 0.6, duration: 0.8 }}
        className="absolute right-4 top-40 w-36 h-48 rounded-3xl bg-gradient-to-br from-[#F4A261] to-[#E76F51] shadow-xl shadow-[#E76F51]/40 z-0 flex items-end p-4"
        aria-hidden
      >
        <Heart className="w-8 h-8 text-white/90" fill="white" strokeWidth={0} />
      </motion.div>
    </div>
  );
}
